import { Injectable } from '@angular/core';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { DrillingHoleInfo } from '@3d-scene/model3d/util/drillinghole/drilling-hole-info';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { ArticleUtils } from '@article/util/article-utils';
import { DrillingHoleService } from '@project/actions/drilling-hole/drilling-hole.service';
import { ProjectDimensionsService } from '@project/components/project/service/project-dimensions.service';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { ProjectModel } from '@project/model/project.model';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { FileUtils } from '@shared/util/file-utils';
import { StringUtils } from '@shared/util/string-utils';
import { Vector3 } from 'three';

@Injectable({
  providedIn: 'root'
})
export class ExportRailInfosService {

  constructor(
    private mountingRailHelper: MountingRailHelper,
    private drillingHoleService: DrillingHoleService,
    private projectDimensionsService: ProjectDimensionsService,
    private projectModel: ProjectModel,
    private localeResourceManager: LocaleResourceManager,
  ) {
  }

  public downloadRailXmlDoc(): void {
    const xmlString = this.createXmlString();
    const projectName = this.projectModel.currentProject.name || this.localeResourceManager.getValueByKey('header.menu.new_project');
    const fileName = StringUtils.sanitizeFileName(`mountingRail ${projectName}`)
    FileUtils.saveTextFile(fileName, xmlString, '.xml');
  }

  public createXmlString(): string {
    return new XMLSerializer().serializeToString(this.createRailXmlDoc());
  }

  private createRailXmlDoc(): XMLDocument {
    const railUsage = this.mountingRailHelper.getMountingRailUsage();

    const drillingHoleInfos = this.drillingHoleService.getDrillingHoleInfos(railUsage);

    const xmlDoc = document.implementation.createDocument(null, 'rail');

    const railElement = this.createRailXmlElement(xmlDoc);
    const drillingHolesElement = xmlDoc.createElement('drillingHoles');
    railElement.append(drillingHolesElement);

    const drillingHoleElements = drillingHoleInfos
      .map((drillingHole) => this.createDrillingHoleXmlElement(xmlDoc, drillingHole));

    drillingHolesElement.append(...drillingHoleElements);
    return xmlDoc;
  }

  private createRailXmlElement(xmlDoc: XMLDocument): HTMLElement {
    const railContainer = this.mountingRailHelper.getMountingRailContainer();
    const railSize = this.projectDimensionsService.getContainerBounds([railContainer]).getSize(new Vector3());
    const material = ArticleUtils.getArticleBehaviorPropertyStringValue(railContainer.article, ArticleBehaviorPropertyDef.MOUNTING_RAIL_MATERIAL);

    const railElement = xmlDoc.documentElement;
    railElement.setAttribute('length', this.units3dToMMString(railSize.x));
    railElement.setAttribute('height', this.units3dToMMString(railSize.y));
    railElement.setAttribute('width', this.units3dToMMString(railSize.z));
    railElement.setAttribute('articleNo', railContainer.article.articleNo);
    railElement.setAttribute('material', material);
    return railElement;
  }

  private createDrillingHoleXmlElement(xmlDoc: XMLDocument, drillingHole: DrillingHoleInfo): HTMLElement {
    const drillingHoleElement = xmlDoc.createElement('drillingHole');

    drillingHoleElement.setAttribute(
      'position',
      this.units3dToMMString(drillingHole.position)
    );

    drillingHoleElement.setAttribute(
      'shapeType',
      drillingHole.shapeType.toString()
    );

    drillingHoleElement.setAttribute(
      'width',
      this.units3dToMMString(drillingHole.size.width)
    );

    drillingHoleElement.setAttribute(
      'length',
      this.units3dToMMString(drillingHole.size.sizeOnLine)
    );

    return drillingHoleElement;
  }

  private units3dToMMString(value: number): string {
    return UnitConverter.fromToRounded('3d', 'mm', value, 1).toString();
  }
}
